import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import toast from 'react-hot-toast';
import { Award, Edit2, Trash2, Plus, Save, X, Gift, Trophy, Upload } from 'lucide-react';

const REWARD_TYPES = [
  { value: 'cash',   label: '💰 Cash / Wallet Credit', color: '#22c55e' },
  { value: 'gift',   label: '🎁 Gift Item',             color: '#a855f7' },
  { value: 'phone',  label: '📱 Phone',                 color: '#3b82f6' },
  { value: 'laptop', label: '💻 Laptop',                color: '#6366f1' },
  { value: 'travel', label: '✈️ Travel Package',        color: '#f59e0b' },
  { value: 'car',    label: '🚗 Car / Vehicle',         color: '#ef4444' },
  { value: 'custom', label: '⭐ Custom Reward',         color: '#ec4899' },
];

const defaultForm = {
  level: '', name: '', directRequired: '', teamRequired: '', l7DirectCount: 3,
  maxLegPercent: 40, setupFeeCommission: '', profitCommission: '',
  rewardEnabled: false, rewardType: 'cash', rewardTitle: '',
  rewardDescription: '', rewardValue: '', rewardImage: '', rewardBadgeColor: '#22c55e'
};

export default function RankConfigPage() {
  const [ranks, setRanks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [achievements, setAchievements] = useState([]);
  const [activeTab, setActiveTab] = useState('ranks');
  const imgRef = useRef();

  const fetchRanks = () => axios.get('/api/admin/ranks').then(r => setRanks(r.data.ranks || []));
  const fetchAchievements = () => axios.get('/api/admin/rank-achievements').then(r => setAchievements(r.data.achievements || []));

  useEffect(() => { fetchRanks(); fetchAchievements(); }, []);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error('Image max 2MB');
    const reader = new FileReader();
    reader.onloadend = () => setForm(p => ({ ...p, rewardImage: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const isLevel8Plus = parseInt(form.level) >= 8;
      const payload = {
        ...form,
        directRequired: isLevel8Plus ? 0 : form.directRequired,
        teamRequired: isLevel8Plus ? 0 : form.teamRequired,
        maxLegPercent: isLevel8Plus ? 0 : form.maxLegPercent,
      };

      if (editing) {
        await axios.put(`/api/admin/ranks/${editing}`, payload);
        toast.success('Rank updated!');
      } else {
        await axios.post('/api/admin/ranks', payload);
        toast.success('Rank created!');
      }
      setEditing(null);
      setShowForm(false);
      setForm(defaultForm);
      fetchRanks();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (rank) => {
    setEditing(rank._id);
    setShowForm(true);
    setForm({
      level: rank.level, name: rank.name,
      directRequired: rank.directRequired, teamRequired: rank.teamRequired, l7DirectCount: rank.l7DirectCount || 3,
      maxLegPercent: rank.maxLegPercent || 40,
      setupFeeCommission: rank.setupFeeCommission, profitCommission: rank.profitCommission,
      rewardEnabled: rank.rewardEnabled || false,
      rewardType: rank.rewardType || 'cash',
      rewardTitle: rank.rewardTitle || '',
      rewardDescription: rank.rewardDescription || '',
      rewardValue: rank.rewardValue || '',
      rewardImage: rank.rewardImage || '',
      rewardBadgeColor: rank.rewardBadgeColor || '#22c55e'
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this rank?')) return;
    await axios.delete(`/api/admin/ranks/${id}`);
    toast.success('Package deleted.');
    fetchRanks();
  };

  const handleAchievementStatus = async (id, status) => {
    const note = status === 'delivered' ? 'Reward delivered successfully!' : `Status: ${status}`;
    try {
      await axios.put(`/api/admin/rank-achievements/${id}/status`, { status, note });
      toast.success('Status updated');
      fetchAchievements();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const pendingAchievements = achievements.filter(a => a.rewardStatus === 'pending' && a.rewardType !== 'cash');

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">Rank & Reward Configuration</h1>
            <p className="text-gray-400 mt-1">Configure rank levels, commissions, and rewards.</p>
          </div>
          <div className="flex gap-2">
            {pendingAchievements.length > 0 && (
              <button onClick={() => setActiveTab('achievements')}
                className="bg-amber-600/20 border border-amber-500/30 text-amber-300 px-4 py-2.5 rounded-xl font-medium flex items-center gap-2 text-sm">
                <Trophy className="w-4 h-4" />
                {pendingAchievements.length} Pending Rewards
              </button>
            )}
            {!showForm && (
              <button onClick={() => { setShowForm(true); setEditing(null); setForm(defaultForm); }}
                className="bg-primary-600 hover:bg-primary-500 text-white px-4 py-2.5 rounded-xl font-medium flex items-center gap-2 transition-colors">
                <Plus className="w-4 h-4" /> Add Rank
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button onClick={() => setActiveTab('ranks')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${activeTab === 'ranks' ? 'bg-primary-600 text-white' : 'bg-dark-700 text-gray-400 border border-white/5'}`}>
            <Award className="w-4 h-4 inline mr-1" /> Ranks ({ranks.length})
          </button>
          <button onClick={() => setActiveTab('achievements')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${activeTab === 'achievements' ? 'bg-primary-600 text-white' : 'bg-dark-700 text-gray-400 border border-white/5'}`}>
            <Trophy className="w-4 h-4 inline mr-1" /> Achievements ({achievements.length})
            {pendingAchievements.length > 0 && (
              <span className="ml-2 bg-amber-500 text-dark-900 text-xs px-1.5 py-0.5 rounded-full font-bold">
                {pendingAchievements.length}
              </span>
            )}
          </button>
        </div>

        {/* Add/Edit Form */}
        {showForm && activeTab === 'ranks' && (
          <div className="bg-dark-700 border border-primary-500/30 rounded-2xl p-6 mb-6 animate-slide-up">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-semibold text-lg">{editing ? 'Edit Rank' : 'Add New Rank'}</h3>
              <button onClick={() => { setShowForm(false); setEditing(null); setForm(defaultForm); }}
                className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <form onSubmit={handleSave}>
              {/* Basic Info */}
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                {(() => {
                  const isLevel8Plus = parseInt(form.level) >= 8;
                  const fields = [
                    { label: 'Level No.', key: 'level', type: 'number', placeholder: '1' },
                    { label: 'Rank Name', key: 'name', type: 'text', placeholder: 'Bronze' },
                    { label: 'Setup Fee Commission %', key: 'setupFeeCommission', type: 'number', placeholder: '20' },
                    { label: 'Profit Commission %', key: 'profitCommission', type: 'number', placeholder: '10' },
                  ];
                  if (!isLevel8Plus) {
                    fields.splice(2, 0,
                      { label: 'Direct Required', key: 'directRequired', type: 'number', placeholder: '3' },
                      { label: 'Team Required', key: 'teamRequired', type: 'number', placeholder: '10' },
                      { label: 'Max Leg % (Default 40)', key: 'maxLegPercent', type: 'number', placeholder: '40' },
                    );
                  }
                  return fields.map(f => (
                    <div key={f.key}>
                      <label className="block text-xs text-gray-400 mb-1">{f.label}</label>
                      <input type={f.type} placeholder={f.placeholder} value={form[f.key]}
                        onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                        className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary-500"
                        required />
                    </div>
                  ));
                })()}
              </div>

              {/* Change 5: Level 8+ L7 Tree Config */}
              {parseInt(form.level) >= 8 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 mb-5">
                  <h4 className="text-amber-300 font-semibold mb-3 flex items-center gap-2">
                    <span>🏆</span> Level {form.level}+ — L7 Tree Configuration
                  </h4>
                  <div className="mb-4 p-4 rounded-2xl bg-dark-600 border border-white/5 text-sm text-gray-300">
                    <p className="font-semibold text-white">Level 8 and above uses L7 tree rules.</p>
                    <p className="mt-1">Do not enter direct/team/leg values for this rank. The user will need to build a tree of Level 7 referrals to qualify.</p>
                    <p className="mt-1 text-sm text-amber-300">Formula: Level {form.level} requires {form.l7DirectCount || 3} direct Level 7 referrals per node, depth {Math.max(parseInt(form.level) - 7, 1)}.</p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1.5">L7 Referrals Required per Node</label>
                      <input type="number" value={form.l7DirectCount || 3}
                        onChange={e => setForm(f => ({ ...f, l7DirectCount: parseInt(e.target.value) }))}
                        className="w-full bg-dark-600 border border-amber-500/30 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-500"
                        min="1" placeholder="3" />
                      <p className="text-gray-500 text-xs mt-1">How many direct Level-7 referrals are required?</p>
                    </div>
                    <div className="bg-dark-600 rounded-xl p-3">
                      <p className="text-gray-400 text-xs mb-1">Tree Depth (Auto)</p>
                      <p className="text-amber-400 font-bold text-2xl">{parseInt(form.level) - 7}</p>
                      <p className="text-gray-500 text-xs">= Level {form.level} minus 7</p>
                    </div>
                  </div>
                  <div className="mt-3 bg-dark-600 rounded-xl p-3 text-xs text-gray-400 space-y-1">
                    <p className="text-amber-300 font-medium">Rule for Level {form.level}:</p>
                    <p>The user's <strong className="text-white">{form.l7DirectCount || 3} direct</strong> referrals must reach <strong className="text-white">Level 7</strong></p>
                    {parseInt(form.level) > 8 && <p className="text-gray-500">And each of those L7 referrals also need {form.l7DirectCount || 3} L7 directs — depth {parseInt(form.level) - 7} levels deep</p>}
                  </div>
                </div>
              )}

              {/* Reward Section */}
              <div className="border-t border-white/10 pt-5 mb-5">
                <div className="flex items-center gap-3 mb-4">
                  <Gift className="w-5 h-5 text-amber-400" />
                  <h4 className="text-white font-semibold">Reward Settings</h4>
                  <label className="flex items-center gap-2 ml-auto cursor-pointer">
                    <span className="text-gray-400 text-sm">Enable Reward</span>
                    <div className="relative">
                      <input type="checkbox" checked={form.rewardEnabled}
                        onChange={e => setForm(p => ({ ...p, rewardEnabled: e.target.checked }))}
                        className="sr-only" />
                      <div className={`w-10 h-5 rounded-full transition-colors ${form.rewardEnabled ? 'bg-primary-500' : 'bg-dark-500'}`}>
                        <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform ${form.rewardEnabled ? 'translate-x-5' : 'translate-x-0.5'}`}></div>
                      </div>
                    </div>
                  </label>
                </div>

                {form.rewardEnabled && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Reward Type */}
                    <div className="md:col-span-2">
                      <label className="block text-xs text-gray-400 mb-2">Reward Type</label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {REWARD_TYPES.map(rt => (
                          <button type="button" key={rt.value}
                            onClick={() => setForm(p => ({ ...p, rewardType: rt.value, rewardBadgeColor: rt.color }))}
                            className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all text-left ${
                              form.rewardType === rt.value
                                ? 'border-primary-500 bg-primary-500/20 text-white'
                                : 'border-white/10 bg-dark-600 text-gray-400 hover:text-white'
                            }`}>
                            {rt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Reward Title</label>
                      <input type="text" value={form.rewardTitle}
                        onChange={e => setForm(p => ({ ...p, rewardTitle: e.target.value }))}
                        className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary-500"
                        placeholder="e.g. iPhone 15 Pro, $50,000 Cash" />
                    </div>

                    {form.rewardType === 'cash' && (
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Cash Amount ($)</label>
                        <input type="number" value={form.rewardValue}
                          onChange={e => setForm(p => ({ ...p, rewardValue: e.target.value }))}
                          className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary-500"
                          placeholder="e.g. 5000" />
                      </div>
                    )}

                    {form.rewardType !== 'cash' && (
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Estimated Value ($)</label>
                        <input type="number" value={form.rewardValue}
                          onChange={e => setForm(p => ({ ...p, rewardValue: e.target.value }))}
                          className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary-500"
                          placeholder="e.g. 100000" />
                      </div>
                    )}

                    <div className="md:col-span-2">
                      <label className="block text-xs text-gray-400 mb-1">Description</label>
                      <textarea value={form.rewardDescription}
                        onChange={e => setForm(p => ({ ...p, rewardDescription: e.target.value }))}
                        className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary-500 resize-none"
                        rows={2} placeholder="Enter reward description and details..." />
                    </div>

                    {/* Reward Image */}
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Reward Image (Optional)</label>
                      <div className="flex gap-2 items-center">
                        {form.rewardImage && (
                          <img src={form.rewardImage} alt="Reward" className="w-12 h-12 rounded-lg object-cover border border-white/10" />
                        )}
                        <button type="button" onClick={() => imgRef.current?.click()}
                          className="flex items-center gap-2 bg-dark-600 border border-white/10 text-gray-300 hover:text-white px-3 py-2 rounded-xl text-xs transition-colors">
                          <Upload className="w-3.5 h-3.5" /> Upload Image
                        </button>
                        {form.rewardImage && (
                          <button type="button" onClick={() => setForm(p => ({ ...p, rewardImage: '' }))}
                            className="text-red-400 text-xs hover:text-red-300">Remove</button>
                        )}
                      </div>
                      <input ref={imgRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                    </div>

                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Badge Color</label>
                      <div className="flex items-center gap-3">
                        <input type="color" value={form.rewardBadgeColor}
                          onChange={e => setForm(p => ({ ...p, rewardBadgeColor: e.target.value }))}
                          className="w-10 h-10 rounded-lg border border-white/10 bg-dark-600 cursor-pointer" />
                        <span className="text-gray-400 text-sm">{form.rewardBadgeColor}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button type="submit" disabled={loading}
                  className="bg-primary-600 hover:bg-primary-500 disabled:bg-primary-800 text-white font-semibold px-6 py-2.5 rounded-xl flex items-center gap-2 transition-colors">
                  {loading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    : <><Save className="w-4 h-4" />{editing ? 'Update' : 'Create'} Rank</>}
                </button>
                <button type="button" onClick={() => { setShowForm(false); setEditing(null); setForm(defaultForm); }}
                  className="px-4 py-2.5 bg-dark-600 border border-white/10 text-gray-400 hover:text-white rounded-xl transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Ranks Table */}
        {activeTab === 'ranks' && (
          <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/5">
                    {['Level','Rank Name','Direct/Team','Max Leg','Setup %','Profit %','Reward','Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-gray-400 font-medium text-left">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ranks.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-12 text-gray-500">No ranks configured</td></tr>
                  ) : ranks.map(r => (
                    <tr key={r._id} className="border-b border-white/5 hover:bg-white/2 transition-colors">
                      <td className="px-4 py-3">
                        <span className="bg-primary-500/20 text-primary-300 text-xs px-2 py-1 rounded-full font-bold">L{r.level}</span>
                      </td>
                      <td className="px-4 py-3 text-white font-semibold">{r.name}</td>
                      <td className="px-4 py-3 text-gray-400 text-xs">{r.directRequired} / {(r.teamRequired||0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-gray-400">{r.maxLegPercent || 40}%</td>
                      <td className="px-4 py-3 text-primary-400 font-semibold">{r.setupFeeCommission}%</td>
                      <td className="px-4 py-3 text-green-400 font-semibold">{r.profitCommission}%</td>
                      <td className="px-4 py-3">
                        {r.rewardEnabled ? (
                          <div className="flex items-center gap-2">
                            {r.rewardImage && (
                              <img src={r.rewardImage} alt="" className="w-6 h-6 rounded object-cover" />
                            )}
                            <div>
                              <span className="text-xs font-medium" style={{ color: r.rewardBadgeColor }}>
                                {r.rewardTitle || r.rewardType}
                              </span>
                              {r.rewardValue > 0 && (
                                <p className="text-gray-500 text-xs">${r.rewardValue.toLocaleString()}</p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-600 text-xs">No Reward</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button onClick={() => handleEdit(r)}
                            className="w-7 h-7 bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 rounded-lg flex items-center justify-center transition-colors">
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleDelete(r._id)}
                            className="w-7 h-7 bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg flex items-center justify-center transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Achievements Tab */}
        {activeTab === 'achievements' && (
          <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-white/5">
              <h3 className="text-white font-semibold">User Rank Achievements</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/5">
                    {['User','Rank','Reward','Achieved At','Status','Action'].map(h => (
                      <th key={h} className="px-4 py-3 text-gray-400 font-medium text-left">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {achievements.length === 0 ? (
                    <tr><td colSpan={6} className="text-center py-12 text-gray-500">No achievements yet</td></tr>
                  ) : achievements.map(a => (
                    <tr key={a._id} className="border-b border-white/5 hover:bg-white/2">
                      <td className="px-4 py-3">
                        <p className="text-white font-medium">{a.user?.name}</p>
                        <p className="text-gray-500 text-xs">{a.user?.email}</p>
                        {a.user?.phone && <p className="text-gray-600 text-xs">{a.user?.phone}</p>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="bg-primary-500/20 text-primary-300 text-xs px-2 py-0.5 rounded-full">L{a.level}</span>
                        <p className="text-white text-xs mt-0.5">{a.rankName}</p>
                      </td>
                      <td className="px-4 py-3">
                        {a.rewardTitle ? (
                          <div>
                            <p className="text-white text-xs font-medium">{a.rewardTitle}</p>
                            {a.rewardValue > 0 && <p className="text-gray-500 text-xs">${a.rewardValue.toLocaleString()}</p>}
                          </div>
                        ) : <span className="text-gray-600 text-xs">Cash Credited</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-xs">
                        {new Date(a.achievedAt).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          a.rewardStatus === 'delivered' || a.rewardStatus === 'cash_credited'
                            ? 'bg-primary-500/20 text-primary-300'
                            : a.rewardStatus === 'processing'
                            ? 'bg-blue-500/20 text-blue-300'
                            : 'bg-amber-500/20 text-amber-300'
                        }`}>
                          {a.rewardStatus?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {a.rewardStatus === 'pending' && (
                          <div className="flex gap-1">
                            <button onClick={() => handleAchievementStatus(a._id, 'processing')}
                              className="px-2 py-1 bg-blue-600/20 text-blue-300 rounded-lg text-xs hover:bg-blue-600/30">
                              Processing
                            </button>
                            <button onClick={() => handleAchievementStatus(a._id, 'delivered')}
                              className="px-2 py-1 bg-primary-600/20 text-primary-300 rounded-lg text-xs hover:bg-primary-600/30">
                              Delivered
                            </button>
                          </div>
                        )}
                        {a.rewardStatus === 'processing' && (
                          <button onClick={() => handleAchievementStatus(a._id, 'delivered')}
                            className="px-2 py-1 bg-primary-600/20 text-primary-300 rounded-lg text-xs hover:bg-primary-600/30">
                            Mark Delivered
                          </button>
                        )}
                        {(a.rewardStatus === 'delivered' || a.rewardStatus === 'cash_credited') && (
                          <span className="text-gray-500 text-xs">✓ Done</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
